// Reflects the count of live visitors in ambient browser chrome: the document
// title and the favicon (a count badge composited over the Openline icon). One
// entry point — setLiveIndicators(count) — called from the Shell whenever the
// live count changes. count <= 0 restores the plain title + icon.

const BASE_TITLE = 'Openline · Agent';
// The dark-background app icon reads fine on light or dark browser chrome, so
// we use it as the single base for the composited badge.
const BASE_ICON = '/logos/openline-icon-light.svg';
const BADGE_COLOR = '#d9522f'; // oxide accent

let baseImg: HTMLImageElement | null = null;
let baseReady = false;

/** The single <link rel="icon"> we own. Replaces the static media-query links
 *  on first use so there's exactly one favicon to drive. */
function faviconLink(): HTMLLinkElement {
  let link = document.getElementById('openline-favicon') as HTMLLinkElement | null;
  if (!link) {
    document
      .querySelectorAll('link[rel="icon"]')
      .forEach((l) => l.parentElement?.removeChild(l));
    link = document.createElement('link');
    link.id = 'openline-favicon';
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
}

function ensureBaseImg(onReady: () => void): void {
  if (baseReady) {
    onReady();
    return;
  }
  if (!baseImg) {
    baseImg = new Image();
    baseImg.onload = () => {
      baseReady = true;
      onReady();
    };
    baseImg.onerror = () => {
      baseImg = null; // allow a retry next time
    };
    baseImg.src = BASE_ICON;
  }
}

function paintBadge(count: number): void {
  ensureBaseImg(() => {
    if (!baseImg) return;
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(baseImg, 0, 0, size, size);

    const label = count > 9 ? '9+' : String(count);
    const r = 19;
    const cx = size - r - 1;
    const cy = size - r - 1;

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = BADGE_COLOR;
    ctx.fill();
    ctx.lineWidth = 5;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = `700 ${label.length > 1 ? 22 : 30}px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, cx, cy + 1);

    const link = faviconLink();
    link.type = 'image/png';
    link.href = canvas.toDataURL('image/png');
  });
}

/** Update the tab title + favicon to reflect `count` live visitors. */
export function setLiveIndicators(count: number): void {
  const n = Math.max(0, count | 0);
  document.title = n > 0 ? `(${n}) ${BASE_TITLE}` : BASE_TITLE;

  if (n <= 0) {
    const link = faviconLink();
    link.type = 'image/svg+xml';
    link.href = BASE_ICON;
    return;
  }
  paintBadge(n);
}
